import React from 'react';
import { Link } from 'react-router-dom';
import SEO from '../../components/SEO';

const CloudImplementation = () => {
  const cloudServices = [
    {
      title: 'Cloud Migration',
      description: 'Seamless migration of your applications, data, and infrastructure to leading cloud platforms with zero downtime.',
      icon: 'M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12'
    },
    {
      title: 'Multi-Cloud Strategy',
      description: 'Design and implement optimal multi-cloud architectures leveraging AWS, Azure, Google Cloud, and hybrid solutions.',
      icon: 'M3 15a4 4 0 004 4h9a5 5 0 10-.1-9.999 5.002 5.002 0 10-9.78 2.096A4.001 4.001 0 003 15z'
    },
    {
      title: 'Infrastructure as Code',
      description: 'Automate infrastructure provisioning and management using Terraform, CloudFormation, and modern DevOps practices.',
      icon: 'M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4'
    },
    {
      title: 'Cloud Security',
      description: 'Implement robust security frameworks including IAM, encryption, compliance monitoring, and threat detection.',
      icon: 'M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z'
    },
    {
      title: 'Cost Optimization',
      description: 'Continuous monitoring and optimization of cloud spending with automated scaling and resource management.',
      icon: 'M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z'
    },
    {
      title: 'Disaster Recovery',
      description: 'Build resilient disaster recovery and business continuity solutions with automated backup and failover systems.',
      icon: 'M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15'
    }
  ];

  const platforms = [
    { name: 'Amazon Web Services', logo: 'AWS', color: 'bg-orange-500' },
    { name: 'Microsoft Azure', logo: 'Azure', color: 'bg-blue-500' },
    { name: 'Google Cloud Platform', logo: 'GCP', color: 'bg-green-500' },
    { name: 'IBM Cloud', logo: 'IBM', color: 'bg-blue-600' },
    { name: 'Oracle Cloud', logo: 'Oracle', color: 'bg-red-500' },
    { name: 'DigitalOcean', logo: 'DO', color: 'bg-blue-400' }
  ];

  const benefits = [
    {
      title: 'Scalability & Flexibility',
      description: 'Scale resources up or down instantly based on demand without infrastructure constraints.',
      metric: '99.9%',
      metricLabel: 'Uptime SLA'
    },
    {
      title: 'Cost Efficiency',
      description: 'Reduce capital expenses and pay only for what you use with optimized resource allocation.',
      metric: '40%',
      metricLabel: 'Average Cost Savings'
    },
    {
      title: 'Enhanced Security',
      description: 'Enterprise-grade security with advanced encryption, compliance certifications, and threat detection.',
      metric: '24/7',
      metricLabel: 'Security Monitoring'
    },
    {
      title: 'Global Reach',
      description: 'Deploy applications closer to your users with global data centers and edge computing.',
      metric: '200+',
      metricLabel: 'Global Regions'
    }
  ];

  const migrationSteps = [
    { step: '1', title: 'Assessment & Planning', description: 'Comprehensive analysis of current infrastructure, applications, and migration readiness' },
    { step: '2', title: 'Strategy Design', description: 'Develop detailed migration strategy, architecture design, and risk mitigation plans' },
    { step: '3', title: 'Proof of Concept', description: 'Validate approach with pilot migrations and performance testing in cloud environment' },
    { step: '4', title: 'Migration Execution', description: 'Execute phased migration with automated tools, minimal downtime, and data integrity' },
    { step: '5', title: 'Optimization', description: 'Fine-tune performance, optimize costs, and implement monitoring and governance' },
    { step: '6', title: 'Ongoing Support', description: 'Continuous management, security updates, and 24/7 technical support' }
  ];

  return (
    <>
      <SEO 
        title="Cloud Implementation Services - AWS, Azure, GCP Migration & Management"
        description="Expert cloud implementation services for seamless migration to AWS, Azure, GCP. Enterprise cloud architecture, security, optimization, and 24/7 managed services."
        keywords="cloud implementation, cloud migration, AWS services, Azure cloud, Google Cloud Platform, multi-cloud strategy, cloud security, infrastructure as code, cloud cost optimization"
      />

      {/* Hero Section */}
      <section className="relative pt-12 pb-12 md:pt-12 md:pb-12 bg-gradient-to-br from-alabaster via-pearl-white to-white overflow-hidden">
        <div className="absolute inset-0 opacity-5">
          <div className="absolute inset-0" style={{
            backgroundImage: 'radial-gradient(circle at 2px 2px, #E63946 1px, transparent 0)',
            backgroundSize: '40px 40px'
          }}></div>
        </div>
        
        <div className="page-container relative">
          <div className="max-w-4xl mx-auto text-center">
            <div className="inline-flex items-center px-4 py-2 bg-blush-light/40 rounded-full mb-6 animate-fade-in">
              <svg className="w-5 h-5 text-primary-red mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 15a4 4 0 004 4h9a5 5 0 10-.1-9.999 5.002 5.002 0 10-9.78 2.096A4.001 4.001 0 003 15z" />
              </svg>
              <span className="text-sm font-semibold text-primary-red">Cloud Transformation Experts</span>
            </div>
            
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6 text-charcoal animate-slide-up">
              Enterprise <span className="text-gradient">Cloud Implementation</span> Services
            </h1>
            
            <p className="text-xl text-titanium leading-relaxed mb-8 animate-slide-up" style={{animationDelay: '0.1s'}}>
              Transform your infrastructure with secure, scalable cloud solutions. Seamless migration to AWS, Azure, GCP with zero downtime and optimized performance.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center animate-slide-up" style={{animationDelay: '0.2s'}}>
              <Link to="/contact" className="btn-primary">
                Start Cloud Migration
              </Link>
              <a href="#services" className="btn-secondary">
                Explore Services
              </a>
            </div>

            {/* Trust Indicators */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mt-12 animate-fade-in" style={{animationDelay: '0.3s'}}>
              {[
                { value: '500+', label: 'Cloud Migrations' },
                { value: '99.9%', label: 'Uptime SLA' },
                { value: '40%', label: 'Cost Reduction' },
                { value: '24/7', label: 'Support' }
              ].map((stat, index) => (
                <div key={index} className="text-center">
                  <div className="text-2xl md:text-3xl font-bold text-primary-red mb-1">{stat.value}</div>
                  <div className="text-sm text-titanium">{stat.label}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Cloud Services Grid */}
      <section id="services" className="py-5 md:py-5 bg-white">
        <div className="page-container">
          <div className="text-center mb-12">
            <h2 className="section-title">Comprehensive Cloud Solutions</h2>
            <p className="section-subtitle max-w-3xl mx-auto">
              End-to-end cloud services designed for enterprise performance, security, and scalability
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {cloudServices.map((service, index) => (
              <div 
                key={index} 
                className="group card hover:shadow-xl hover:border-primary-red/20 transition-all duration-300"
              >
                <div className="w-14 h-14 bg-gradient-to-br from-primary-red/10 to-coral-mist/10 rounded-xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300">
                  <svg className="w-7 h-7 text-primary-red" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d={service.icon} />
                  </svg>
                </div>
                <h3 className="text-xl font-semibold mb-3 text-charcoal group-hover:text-primary-red transition-colors">
                  {service.title}
                </h3>
                <p className="text-titanium leading-relaxed">
                  {service.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Cloud Platforms */}
      <section className="py-5 md:py-5 bg-alabaster">
        <div className="page-container">
          <div className="text-center mb-12">
            <h2 className="section-title">Multi-Cloud Expertise</h2>
            <p className="section-subtitle max-w-3xl mx-auto">
              Certified partnerships with leading cloud providers for optimal platform selection
            </p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-6">
            {platforms.map((platform, index) => (
              <div 
                key={index}
                className="bg-white rounded-xl p-6 text-center hover:shadow-lg transition-all duration-300 group cursor-pointer"
              >
                <div className={`w-16 h-16 ${platform.color} rounded-lg flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform`}>
                  <span className="text-white font-bold text-sm">{platform.logo}</span>
                </div>
                <p className="text-sm font-medium text-charcoal">{platform.name}</p>
              </div>
            ))}
          </div>

          <div className="mt-12 text-center">
            <p className="text-titanium mb-4">Certified experts in AWS, Azure, Google Cloud, and hybrid cloud solutions</p>
            <div className="flex flex-wrap justify-center gap-3">
              {['AWS Certified', 'Azure Expert', 'Google Cloud Partner', 'Kubernetes Certified'].map((cert, index) => (
                <span key={index} className="px-4 py-2 bg-white rounded-full text-sm text-charcoal border border-platinum">
                  {cert}
                </span>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-5 md:py-5 bg-white">
        <div className="page-container">
          <div className="text-center mb-12">
            <h2 className="section-title">Cloud Transformation Benefits</h2>
            <p className="section-subtitle max-w-3xl mx-auto">
              Measurable business value from enterprise cloud adoption
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {benefits.map((benefit, index) => (
              <div key={index} className="text-center group">
                <div className="mb-6">
                  <div className="text-4xl md:text-5xl font-bold text-primary-red mb-2 group-hover:scale-110 transition-transform">
                    {benefit.metric}
                  </div>
                  <div className="text-sm text-titanium font-medium">{benefit.metricLabel}</div>
                </div>
                <h3 className="text-xl font-semibold mb-3 text-charcoal">{benefit.title}</h3>
                <p className="text-titanium">{benefit.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Migration Process */}
      <section className="py-5 md:py-5 bg-gradient-to-br from-charcoal to-graphite text-white">
        <div className="page-container">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-6">
              Our Cloud Migration Process
            </h2>
            <p className="text-xl text-cool-grey max-w-3xl mx-auto">
              Proven methodology for seamless, risk-free cloud transformation
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-6xl mx-auto">
            {migrationSteps.map((item, index) => (
              <div 
                key={index}
                className="relative bg-white/5 backdrop-blur-sm rounded-xl p-6 border border-white/10 hover:bg-white/10 hover:border-coral-mist/30 transition-all duration-300"
              >
                <div className="absolute -top-4 -left-4 w-12 h-12 bg-gradient-to-br from-primary-red to-coral-mist rounded-full flex items-center justify-center text-white font-bold text-lg shadow-lg">
                  {item.step}
                </div>
                <h3 className="text-lg font-semibold mb-3 mt-2">{item.title}</h3>
                <p className="text-cool-grey text-sm leading-relaxed">{item.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features Comparison */}
      <section className="py-5 md:py-5 bg-alabaster">
        <div className="page-container">
          <div className="max-w-5xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="section-title">Why Choose Our Cloud Services</h2>
              <p className="section-subtitle">
                Enterprise-grade capabilities that set us apart
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-8">
              {[
                {
                  title: 'Zero-Downtime Migration',
                  description: 'Advanced migration techniques ensure continuous operations throughout the transition process.',
                  icon: 'M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z'
                },
                {
                  title: 'Automated Scaling',
                  description: 'Intelligent auto-scaling based on demand patterns optimizes performance and costs.',
                  icon: 'M13 7h8m0 0v8m0-8l-8 8-4-4-6 6'
                },
                {
                  title: 'Compliance Ready',
                  description: 'Pre-configured compliance frameworks for HIPAA, SOC 2, GDPR, and industry standards.',
                  icon: 'M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z'
                },
                {
                  title: 'Cost Transparency',
                  description: 'Real-time cost tracking, budgeting tools, and optimization recommendations.',
                  icon: 'M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z'
                },
                {
                  title: 'Disaster Recovery',
                  description: 'Automated backup, geo-redundancy, and rapid recovery capabilities built-in.',
                  icon: 'M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15'
                },
                {
                  title: '24/7 Expert Support',
                  description: 'Round-the-clock monitoring, incident response, and technical support from certified experts.',
                  icon: 'M18.364 5.636l-3.536 3.536m0 5.656l3.536 3.536M9.172 9.172L5.636 5.636m3.536 9.192L5.636 18.364M12 2.5v7M12 14.5v7M2.5 12h7M14.5 12h7'
                }
              ].map((feature, index) => (
                <div key={index} className="flex items-start space-x-4 bg-white p-6 rounded-xl hover:shadow-lg transition-all duration-300">
                  <div className="w-12 h-12 bg-blush-light/40 rounded-lg flex items-center justify-center flex-shrink-0">
                    <svg className="w-6 h-6 text-primary-red" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d={feature.icon} />
                    </svg>
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold mb-2 text-charcoal">{feature.title}</h3>
                    <p className="text-titanium">{feature.description}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Use Cases */}
      <section className="py-5 md:py-5 bg-white">
        <div className="page-container">
          <div className="text-center mb-12">
            <h2 className="section-title">Cloud Solutions by Industry</h2>
            <p className="section-subtitle max-w-3xl mx-auto">
              Tailored cloud implementations for your specific industry requirements
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            {[
              { industry: 'Healthcare', use: 'HIPAA-compliant cloud infrastructure for patient data and telemedicine platforms' },
              { industry: 'Financial Services', use: 'PCI-DSS certified environments for secure transaction processing' },
              { industry: 'E-commerce', use: 'Auto-scaling retail platforms handling millions of concurrent users' },
              { industry: 'Manufacturing', use: 'IoT data processing and real-time analytics for smart factories' },
              { industry: 'Education', use: 'Scalable learning management systems with global content delivery' },
              { industry: 'Media', use: 'High-performance content delivery and video streaming infrastructure' }
            ].map((item, index) => (
              <div key={index} className="card group hover:shadow-xl transition-all duration-300">
                <h3 className="text-lg font-semibold mb-3 text-charcoal group-hover:text-primary-red transition-colors">
                  {item.industry}
                </h3>
                <p className="text-titanium text-sm">{item.use}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-5 md:py-5 bg-gradient-to-r from-primary-red to-deep-garnet text-white">
        <div className="page-container text-center">
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-6">
            Ready to Transform Your Infrastructure?
          </h2>
          <p className="text-xl mb-8 opacity-90 max-w-3xl mx-auto">
            Get a free cloud assessment and migration strategy from our certified cloud architects. No commitment required.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/contact" className="bg-white text-primary-red px-8 py-4 rounded-lg text-lg font-semibold hover:bg-pearl-white transition-all duration-300 shadow-lg">
              Schedule Free Consultation
            </Link>
            <Link to="/ai-hub" className="border-2 border-white text-white px-8 py-4 rounded-lg text-lg font-semibold hover:bg-white hover:text-primary-red transition-all duration-300">
              Explore AI Solutions
            </Link>
          </div>

          {/* Trust Badges */}
          <div className="mt-12 pt-8 border-t border-white/20">
            <p className="text-sm mb-4 opacity-75">Trusted by Fortune 500 companies worldwide</p>
            <div className="flex flex-wrap justify-center gap-6 opacity-60">
              {['ISO 27001', 'SOC 2 Type II', 'AWS Partner', 'Azure Expert', 'Google Cloud'].map((badge, index) => (
                <span key={index} className="text-sm">{badge}</span>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Related Services */}
      <section className="py-5 bg-alabaster">
        <div className="page-container">
          <h2 className="text-2xl font-bold mb-8 text-charcoal text-center">Related Services</h2>
          <div className="grid md:grid-cols-3 gap-6 max-w-4xl mx-auto">
            <Link to="/services/cybersecurity" className="card hover:shadow-xl transition-all duration-300 group">
              <h3 className="text-lg font-semibold mb-2 text-charcoal group-hover:text-primary-red transition-colors">
                Cloud Security
              </h3>
              <p className="text-sm text-titanium mb-3">
                Protect your cloud infrastructure with enterprise-grade security
              </p>
              <span className="text-primary-red font-semibold text-sm">Learn More →</span>
            </Link>

            <Link to="/services/data-analytics" className="card hover:shadow-xl transition-all duration-300 group">
              <h3 className="text-lg font-semibold mb-2 text-charcoal group-hover:text-primary-red transition-colors">
                Cloud Analytics
              </h3>
              <p className="text-sm text-titanium mb-3">
                Transform data into insights with cloud-native analytics
              </p>
              <span className="text-primary-red font-semibold text-sm">Learn More →</span>
            </Link>

            <Link to="/services/ai-machine-learning" className="card hover:shadow-xl transition-all duration-300 group">
              <h3 className="text-lg font-semibold mb-2 text-charcoal group-hover:text-primary-red transition-colors">
                AI in the Cloud
              </h3>
              <p className="text-sm text-titanium mb-3">
                Deploy scalable AI/ML models on cloud infrastructure
              </p>
              <span className="text-primary-red font-semibold text-sm">Learn More →</span>
            </Link>
          </div>
        </div>
      </section>
    </>
  );
};

export default CloudImplementation;